import { useRef, Fragment, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Field } from 'formik';
import styled, { withTheme } from 'styled-components';
import { useCookies } from 'react-cookie';
import * as yup from 'yup';

import {
    FovInput,
    FovFieldError,
    Tags,
    VerticalSpacer,
    FovButton,
    FovModal,
} from 'components';

import { AVAILABILITY_FORMATS } from 'utils';

import { createCreatorAdmin } from 'store/actions/admin';

const ButtonContainer = styled('div')`
    display: inline-flex;
    width: 100%;
    max-width: 25rem;
`;

const InputContainer = styled('div')`
    max-width: 25rem;
`;

const AddCreatorForm = withTheme(props => {
    const { isVisible, onClose } = props;

    const dispatch = useDispatch();
    const formikRef = useRef();

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const setRef = form => {
        if (!formikRef.current) {
            formikRef.current = form;
        }
    };

    const handleSubmit = useCallback(args => {
        const { values, setSubmitting } = args;
        const { displayName, purchaseFormats, subscriptionFormats } = values;
        const subscriptionArr = subscriptionFormats.split(',');
        const purchaseArr = purchaseFormats.split(',');

        dispatch(createCreatorAdmin(
            token,
            { name: displayName, purchaseFormats: purchaseArr, subscriptionFormats: subscriptionArr },
        ))
            .finally(() => {
                setSubmitting(false);
                onClose();
            });
    }, []);

    const defaults = {
        displayName: '',
        subscriptionFormats: AVAILABILITY_FORMATS.join(','),
        purchaseFormats: AVAILABILITY_FORMATS.join(','),
    };

    const schema = yup.object().shape({
        displayName: yup.string().required('A display name is required.'),
        subscriptionFormats: yup.string().required('At least one format is required.')
            .test('is-valid',
                `Each subscription format must be in ${AVAILABILITY_FORMATS.join (', ')}`,
                value => value?.split?.(',').every(format => AVAILABILITY_FORMATS.includes(format)),
            ),
        purchaseFormats: yup.string().required('At least one format is required.')
            .test('is-valid',
                `Each purchase format must be in ${AVAILABILITY_FORMATS.join (', ')}`,
                value => value?.split?.(',').every(format => AVAILABILITY_FORMATS.includes(format)),
            ),
    });


    return (
        <FovModal
            title='Create a creator'
            visible={isVisible}
            onClose={onClose}
        >
        <Fragment>
            <Formik
                initialValues={defaults}
                validationSchema={schema}
                onSubmit={(values, handlers) => {
                    const { setSubmitting } = handlers;
                    handleSubmit({ values, setSubmitting });
                }}
                validateOnChange={true}
            >
            {form => (
                <Fragment>
                {setRef(form)}
                Display Name
                <Field type='text' name='displayName'>
                {data => (
                    <InputContainer>
                        <FovInput
                            value={data.field.value}
                            placeholder={''}
                            {...data.field}
                            onChange={e => {
                                const newVal = e.target.value;
                                form.setFieldValue(data.field.name, newVal);
                            }}
                        />
                    </InputContainer>
                )}
                </Field>
                <FovFieldError name='displayName' />

                Subscription Formats
                <VerticalSpacer size={10} />

                <Field type='input' name='subscriptionFormats'>
                {data => (
                    <Tags
                        values={data.field.value ? data.field.value : []}
                        newTagText='Add format'
                        onChange={(e, domains) => {
                            form.setFieldValue(data.field.name, domains);
                        }}
                    />
                )}
                </Field>
                <FovFieldError name='subscriptionFormats' />

                Purchase Formats
                <VerticalSpacer size={10} />

                <Field type='input' name='purchaseFormats'>
                {data => (
                    <Tags
                        values={data.field.value ? data.field.value : []}
                        newTagText='Add format'
                        onChange={(e, domains) => {
                            form.setFieldValue(data.field.name, domains);
                        }}
                    />
                )}
                </Field>
                <FovFieldError name='purchaseFormats' />
                <ButtonContainer>
                    <FovButton
                        type='submit'
                        disabled={form.isSubmitting}
                        onClick={form.handleSubmit}
                    >
                    Add Creator
                    </FovButton>
                </ButtonContainer>


                </Fragment>
            )}
            </Formik>
        </Fragment>
        </FovModal>
    );
});

export { AddCreatorForm };
